import type ReactAce from 'react-ace';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Placeholder } from '../types';

type OverlayPosition = {
  top: number;
  left: number;
  width: number;
  height: number;
  visible: boolean;
};

export const useEditorOverlays = (placeholders: Map<string, Placeholder>) => {
  const editorRef = useRef<ReactAce>(null);
  const overlayContainerRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout>();

  const [overlayPositions, setOverlayPositions] = useState<
    Map<string, OverlayPosition>
  >(() => new Map());
  const [isScrolling, setIsScrolling] = useState(false);

  // Find and position placeholders in the editor
  const findPlaceholderPositions = useCallback(() => {
    const editor = editorRef.current?.editor;
    const overlayContainer = overlayContainerRef.current;

    if (!editor || !overlayContainer || placeholders.size === 0) {
      setOverlayPositions(new Map());
      return;
    }

    const editorValue = editor.getValue();
    const newPositions = new Map<string, OverlayPosition>();

    // Get visible line range to reduce computation
    const firstVisibleRow = editor.renderer.getFirstVisibleRow();
    const lastVisibleRow = editor.renderer.getLastVisibleRow();
    const lines = editorValue.split('\n');

    // Get bounding rectangles
    const editorRect = editor.container.getBoundingClientRect();
    const overlayRect = overlayContainer.getBoundingClientRect();

    placeholders.forEach((_, placeholderId) => {
      // Only search in visible lines to reduce computation
      for (
        let row = Math.max(0, firstVisibleRow - 2);
        row <= Math.min(lines.length - 1, lastVisibleRow + 2);
        row += 1
      ) {
        const line = lines[row];
        const columnIndex = line?.indexOf(placeholderId);

        if (columnIndex !== undefined && columnIndex !== -1) {
          try {
            // Get screen coordinates for the placeholder position
            const startCoords = editor.renderer.textToScreenCoordinates(
              row,
              columnIndex
            );
            const endCoords = editor.renderer.textToScreenCoordinates(
              row,
              columnIndex + placeholderId.length
            );

            // Check if the position is within the editor's visible viewport
            const isWithinViewport =
              startCoords.pageY >= editorRect.top &&
              startCoords.pageY <= editorRect.bottom &&
              startCoords.pageX >= editorRect.left &&
              startCoords.pageX <= editorRect.right;

            // Calculate position relative to the overlay container
            const relativeTop = startCoords.pageY - overlayRect.top;
            const relativeLeft = startCoords.pageX - overlayRect.left;

            newPositions.set(placeholderId, {
              top: relativeTop,
              left: relativeLeft,
              width: endCoords.pageX - startCoords.pageX,
              height: editor.renderer.lineHeight,
              visible: isWithinViewport,
            });
            break; // Only position overlay for first occurrence
          } catch (_error) {
            // Handle cases where coordinates can't be calculated
            newPositions.set(placeholderId, {
              top: 0,
              left: 0,
              width: 0,
              height: 0,
              visible: false,
            });
          }
        }
      }
    });

    setOverlayPositions(newPositions);
  }, [placeholders]);

  // Update overlay positions when editor content or scroll changes
  const updateOverlayPositions = useCallback(() => {
    findPlaceholderPositions();
  }, [findPlaceholderPositions]);

  // Set up editor event listeners
  useEffect(() => {
    const editor = editorRef.current?.editor;
    if (!editor) return undefined;

    const handleEditorChangeEvent = () => {
      // Use requestAnimationFrame for smoother updates
      requestAnimationFrame(updateOverlayPositions);
    };

    const handleScroll = () => {
      // Hide overlays immediately when scrolling starts
      setIsScrolling(true);

      // Clear previous timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // Show overlays again after scrolling stops
      scrollTimeoutRef.current = setTimeout(() => {
        setIsScrolling(false);
        updateOverlayPositions();
      }, 150);
    };

    const handleResize = () => {
      updateOverlayPositions();
    };

    // Add event listeners
    editor.on('change', handleEditorChangeEvent);
    editor.getSession().on('changeScrollLeft', handleScroll);
    editor.getSession().on('changeScrollTop', handleScroll);
    editor.renderer.on('resize', handleResize);

    // Initial position calculation with requestAnimationFrame
    requestAnimationFrame(() => {
      setTimeout(updateOverlayPositions, 50);
    });

    return () => {
      editor.off('change', handleEditorChangeEvent);
      editor.getSession().off('changeScrollLeft', handleScroll);
      editor.getSession().off('changeScrollTop', handleScroll);
      editor.renderer.off('resize', handleResize);
    };
  }, [updateOverlayPositions]);

  // Update positions when placeholders change
  useEffect(() => {
    updateOverlayPositions();
  }, [placeholders, updateOverlayPositions]);

  return {
    editorRef,
    overlayContainerRef,
    overlayPositions,
    isScrolling,
  };
};
